import React, { useMemo, useState } from 'react';
import {
  Body,
  Caption,
  DatePicker,
  Headline,
  LoadingSpinner,
  timeUtils,
} from '@sato/components';
import { useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { WaterUsageChart } from '@components/Charts/WaterUsageChart';
import { apartmentStore } from '@stores/apartmentStore';
import { localizationStore, t } from '@stores/localization';

import { WaterUsagePrices } from './components/WaterUsagePrices';
import { WaterUsageSummary } from './components/WaterUsageSummary';
import {
  deserializeMeasurements,
  fetchWaterUsageRange,
  getDailyMeasurementsByMonthQuery,
  getWaterUsageRangeQueryKey,
} from './WaterUsage.queries';

export const WaterUsage = observer(() => {
  const apartmentId = apartmentStore.selectedApartmentID;

  const [selectedMonth, setSelectedMonth] = useState<Date | undefined>(
    new Date()
  );

  const { data: waterUsageRange, isLoading: isRangeLoading } = useQuery({
    queryKey: getWaterUsageRangeQueryKey(apartmentId ?? ''),
    queryFn: () => fetchWaterUsageRange(apartmentId),
    enabled: !!apartmentId,
  });

  const {
    data,
    isLoading: isMeasurementsLoading,
    isFetching: isMeasurementsFetching,
  } = useQuery({
    ...getDailyMeasurementsByMonthQuery({
      apartmentId,
      selectedDate: selectedMonth,
    }),
    enabled: !!apartmentId && !!selectedMonth,
  });

  const measurements = useMemo(
    () => (data ? deserializeMeasurements(data) : undefined),
    [data]
  );

  if (isRangeLoading || isMeasurementsLoading) {
    return (
      <div className="flex h-[50vh] w-full items-center justify-center">
        <LoadingSpinner size={64} />
      </div>
    );
  }

  // User has no water usage data
  if (!waterUsageRange) {
    const isFixedPricedWaterRate = ['INCLUDED_IN_RENT', 'PER_PERSON'].some(
      (waterRateType) =>
        waterRateType ===
        apartmentStore.selectedApartment?.apartment.waterRateType
    );

    return isFixedPricedWaterRate ? (
      <Body className="max-w-3xl">
        {localizationStore.getTranslation('waterUsage.noMeterDataAvailable')}
      </Body>
    ) : null;
  }

  return (
    <div className="flex w-full !max-w-full flex-col items-center gap-3 lg:px-7">
      <Headline
        level={3}
        className="!font-header !text-5 uppercase lg:text-center"
      >
        {t('waterUsageDatePickerTitle')}
      </Headline>

      <DatePicker
        variant="month"
        onChange={(value) =>
          setSelectedMonth(value ? new Date(value) : undefined)
        }
        value={selectedMonth}
        minValue={waterUsageRange?.start}
        maxValue={waterUsageRange?.end}
        className="w-full md:w-[30rem]"
      />

      {measurements ? (
        <div className="mt-5 w-full">
          <WaterUsageChart data={measurements} />

          {isMeasurementsFetching ? (
            <LoadingSpinner className="mx-auto" size={32} />
          ) : (
            <Body className="mt-2 text-center">
              {t('updated')}{' '}
              {timeUtils.formatDate({
                date: measurements.updated,
                dateFormat: timeUtils.DATE_FORMATS.DATE_AND_TIME_SHORT,
                locale: localizationStore.language,
              })}
            </Body>
          )}

          <div className="flex flex-col gap-6">
            <WaterUsageSummary className="mt-5" measurements={measurements} />

            {data?.usagePrices && <WaterUsagePrices {...data.usagePrices} />}

            <Caption className="!block text-center">
              {t('waterUsageUnitOfMeasurementTip')}
            </Caption>
          </div>
        </div>
      ) : (
        <div>{t('waterUsageNoResults')}</div>
      )}
    </div>
  );
});
